import { XStream } from '@ant-design/x-sdk'

import type { ChatAttachment } from 'src/components/chat'
import {
  getSquadConfig,
  SquadConfigError,
  squadFetch,
  SquadRequestError
} from 'src/services/squadAgent'
import { browserTimezone } from 'src/utils/browserTimezone'

export {
  getSquadConfig,
  isSquadConfigured,
  SquadConfigError,
  SquadRequestError
} from 'src/services/squadAgent'

export type SquadStreamEvent =
  | { type: 'run'; runId: string; threadId: string }
  | { type: 'token'; text: string }
  | { type: 'reasoning'; text: string; name?: string }
  | { type: 'tool_call'; name?: string; payload: unknown }
  | { type: 'tool_result'; name?: string; payload: unknown }
  | { type: 'approval_required'; approvalId?: string; payload: unknown }
  | { type: 'paused'; runId?: string }
  | { type: 'usage'; payload: unknown }
  | { type: 'done'; runId?: string }
  | { type: 'error'; error: string }
  | { type: 'file'; attachment: ChatAttachment }

const QUESTION_PENDING_CODE = 'question_pending'

const jsonBodyOf = (err: unknown, status: number) => {
  if (!(err instanceof SquadRequestError) || err.status !== status) return null
  try {
    const body: unknown = JSON.parse(err.body)

    return body && typeof body === 'object'
      ? (body as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/** 409 while the agent waits on its own `ask_user` question: an answer resumes
 * a tool call, which cannot carry files. */
export const isQuestionPendingError = (err: unknown): boolean =>
  jsonBodyOf(err, 409)?.code === QUESTION_PENDING_CODE

/** 400 with a `file_ids` field error: the attached files cannot go with this
 * message (not ready, already sent, another agent's). */
export const isFileRefusal = (err: unknown): boolean =>
  'file_ids' in (jsonBodyOf(err, 400) ?? {})

type StreamChatOptions = {
  appToken: string
  agentId: string
  message: string
  threadId?: string | null
  fileIds?: string[]
  signal?: AbortSignal
}

type ResumeAction = 'accept' | 'accept_always' | 'reject' | 'answer'

type ResumeChatOptions = {
  appToken: string
  approvalId: string
  action: ResumeAction
  overrides?: Record<string, unknown>
  reason?: string
  signal?: AbortSignal
}

export const mapEvent = (
  name: string,
  raw: string
): SquadStreamEvent | null => {
  let data: Record<string, unknown> = {}
  try {
    data = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
  } catch {
    return null
  }

  switch (name) {
    case 'run':
      return {
        type: 'run',
        runId: String(data.run_id ?? ''),
        threadId: String(data.thread_id ?? '')
      }
    case 'token':
      return { type: 'token', text: String(data.text ?? '') }
    case 'reasoning':
      return {
        type: 'reasoning',
        text: String(data.text ?? ''),
        name: data.name ? String(data.name) : undefined
      }
    case 'tool_call':
      return { type: 'tool_call', name: data.name as string, payload: data }
    case 'tool_result':
      return { type: 'tool_result', name: data.name as string, payload: data }
    case 'approval_required':
      return {
        type: 'approval_required',
        approvalId: data.approval_id ? String(data.approval_id) : undefined,
        payload: data
      }
    case 'paused':
      return {
        type: 'paused',
        runId: data.run_id ? String(data.run_id) : undefined
      }
    case 'usage':
      return { type: 'usage', payload: data }
    case 'done':
      return {
        type: 'done',
        runId: data.run_id ? String(data.run_id) : undefined
      }
    case 'error':
      return { type: 'error', error: String(data.error ?? 'runtime error') }
    case 'file':
      return {
        type: 'file',
        attachment: {
          id: String(data.file_id ?? ''),
          filename: String(data.filename ?? ''),
          mimeType: String(data.mime_type ?? ''),
          sizeBytes: Number(data.size_bytes ?? 0),
          source: 'agent'
        }
      }
    default:
      return null
  }
}

const CARRIAGE_RETURN = '\r'.charCodeAt(0)

const stripCarriageReturns = (): TransformStream<Uint8Array, Uint8Array> =>
  new TransformStream({
    transform(chunk, controller) {
      const normalized = chunk.filter((byte) => byte !== CARRIAGE_RETURN)
      if (normalized.length > 0) controller.enqueue(normalized)
    }
  })

export async function* parseSseStream(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<SquadStreamEvent, void, void> {
  const readableStream = body.pipeThrough(stripCarriageReturns())

  for await (const frame of XStream({ readableStream })) {
    const eventName = typeof frame.event === 'string' ? frame.event : 'message'
    const data = typeof frame.data === 'string' ? frame.data : ''
    const parsed = mapEvent(eventName, data)

    if (parsed) yield parsed
  }
}

const streamOrThrow = async (
  response: Response
): Promise<ReadableStream<Uint8Array>> => {
  if (!response.ok || !response.body) {
    const body = await response.text().catch(() => '')
    throw new SquadRequestError(response.status, body)
  }

  return response.body
}

export async function* streamChat({
  appToken,
  agentId,
  message,
  threadId,
  fileIds,
  signal
}: StreamChatOptions): AsyncGenerator<SquadStreamEvent, void, void> {
  const { agentId: defaultAgent } = getSquadConfig()
  const resolvedAgent = agentId || defaultAgent
  if (!resolvedAgent) {
    throw new SquadConfigError('No agent id configured')
  }

  const response = await squadFetch(
    '/api/chat/',
    {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: JSON.stringify({
        agent_id: resolvedAgent,
        message,
        timezone: browserTimezone(),
        ...(threadId ? { thread_id: threadId } : {}),
        ...(fileIds?.length ? { file_ids: fileIds } : {})
      })
    },
    appToken
  )

  yield* parseSseStream(await streamOrThrow(response))
}

type StreamRunOptions = {
  appToken: string
  runId: string
  since: number
  signal?: AbortSignal
}

export async function* streamRun({
  appToken,
  runId,
  since,
  signal
}: StreamRunOptions): AsyncGenerator<SquadStreamEvent, void, void> {
  const response = await squadFetch(
    `/api/runs/${runId}/stream/?since=${since}`,
    {
      method: 'GET',
      signal,
      headers: {
        Accept: 'text/event-stream'
      }
    },
    appToken
  )

  yield* parseSseStream(await streamOrThrow(response))
}

export async function* resumeChat({
  appToken,
  approvalId,
  action,
  overrides,
  reason,
  signal
}: ResumeChatOptions): AsyncGenerator<SquadStreamEvent, void, void> {
  const response = await squadFetch(
    '/api/chat/resume/',
    {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      },
      body: JSON.stringify({
        approval_id: approvalId,
        action,
        timezone: browserTimezone(),
        ...(overrides ? { overrides } : {}),
        ...(reason ? { reason } : {})
      })
    },
    appToken
  )

  yield* parseSseStream(await streamOrThrow(response))
}
